import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPrinter, FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, Page, Card, Button, Kicker, Serif, Alert, EmptyState } from '../common/ui';

/**
 * Printable "family page": for each interactive lesson of the student's course it gathers
 * the "Tell it at home" task, the words of the day, the recap and the golden rules,
 * so parents can read in two minutes what their child learned — and do the home task together.
 *
 * Query params: ?course=<courseId>  (defaults to the logged-in student's course)
 *               ?lesson=<lessonId>  (print only one lesson)
 */

const CHAR_NAMES = { trace: 'Trace', mina: 'Mina', flash: 'Dr. Flash', prince: 'Prince', juss: 'Juss', cass: 'Cass', profz: 'Professor Z', tim: 'Tim', ben: 'Ben', jason: 'Jason', jackie: 'Jackie' };

function extract(lesson) {
  const slides = Array.isArray(lesson.interactiveSlides) ? lesson.interactiveSlides : [];
  const homeTasks = [];
  const glossary = [];
  const recap = [];
  const rules = [];
  const objectives = [];
  const quotes = [];
  slides.forEach((s) => {
    if (s.homeTask) homeTasks.push(s.homeTask);
    if (Array.isArray(s.glossary)) s.glossary.forEach((g) => glossary.push(typeof g === 'object' ? g : { term: g }));
    if (Array.isArray(s.recapItems)) s.recapItems.forEach((r) => recap.push(r));
    if (Array.isArray(s.rules)) s.rules.forEach((r) => rules.push(r));
    if (Array.isArray(s.objectiveItems) && objectives.length === 0) s.objectiveItems.forEach((o) => objectives.push(o));
    if (s.quote) quotes.push({ text: s.quote, by: s.quoteBy });
  });
  return { homeTasks, glossary, recap, rules, objectives, quotes };
}

// The page is printed by parents: light ivory/white surfaces, ink text, gold accents only.
const css = `
  .fam-root { font-family: ${BRAND.font}; color: ${BRAND.ink}; }
  .fam-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
  .fam-head { display: flex; align-items: center; gap: 18px; margin-bottom: 22px; }
  .fam-head img { width: 68px; height: 68px; flex-shrink: 0; }
  .fam-head h1 { font-size: clamp(1.35rem, 2.6vw, 1.7rem); font-weight: 900; margin: 6px 0 4px; line-height: 1.15; color: ${BRAND.ink}; }
  .fam-wordmark { display: flex; align-items: center; gap: 10px; font-size: 0.72rem; letter-spacing: 0.3em; text-transform: uppercase; font-weight: 800; color: ${BRAND.gold}; }
  .fam-wordmark::after { content: ''; width: 34px; height: 1px; background: ${BRAND.gold}; }
  .fam-lesson { page-break-inside: avoid; break-inside: avoid; margin-bottom: 20px; }
  .fam-lesson h2 { margin: 2px 0 4px; font-size: 1.3rem; font-weight: 900; color: ${BRAND.ink}; }
  .fam-lesson .desc { margin: 0 0 12px; font-weight: 500; color: ${BRAND.inkSoft}; font-size: 0.92rem; line-height: 1.5; }
  .fam-sec { margin-top: 16px; }
  .fam-sec h3 { margin: 0 0 8px; font-size: 0.74rem; letter-spacing: 0.16em; text-transform: uppercase; font-weight: 900; color: ${BRAND.gold}; display: flex; align-items: center; gap: 8px; }
  .fam-sec h3::after { content: ''; flex: 1; height: 1px; background: ${BRAND.line}; }
  .fam-task { padding: 14px 16px; border-radius: 14px; background: rgba(243,220,180,0.35); border: 1px solid ${BRAND.gold}; font-weight: 700; font-size: 1rem; line-height: 1.5; color: ${BRAND.ink}; }
  .fam-task .chk { display: inline-block; width: 18px; height: 18px; border: 2px solid ${BRAND.ink}; border-radius: 5px; vertical-align: -3px; margin-right: 10px; }
  .fam-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .fam-card { padding: 11px 13px; border-radius: 12px; background: ${BRAND.ivory}; border: 1px solid ${BRAND.lineIvory}; }
  .fam-card b { display: block; font-size: 0.9rem; color: ${BRAND.ink}; margin-bottom: 2px; }
  .fam-card span { font-size: 0.84rem; font-weight: 500; color: ${BRAND.inkSoft}; line-height: 1.4; }
  .fam-words { display: flex; flex-wrap: wrap; gap: 8px; }
  .fam-word { padding: 7px 12px; border-radius: 999px; background: ${BRAND.ivory}; border: 1px solid ${BRAND.lineIvory}; font-size: 0.86rem; }
  .fam-word b { color: ${BRAND.ink}; }
  .fam-word span { color: ${BRAND.inkSoft}; font-weight: 500; }
  .fam-rules { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 7px; }
  .fam-rules li { display: flex; gap: 10px; align-items: flex-start; font-size: 0.9rem; }
  .fam-rules li .n { width: 24px; height: 24px; border-radius: 50%; background: linear-gradient(135deg, ${BRAND.goldLight}, ${BRAND.gold}); color: ${BRAND.navy900}; font-weight: 900; font-size: 0.74rem; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .fam-rules li b { color: ${BRAND.ink}; }
  .fam-rules li span { color: ${BRAND.inkSoft}; font-weight: 500; }
  .fam-quote { font-family: ${BRAND.serif}; font-style: italic; font-size: 1.18rem; line-height: 1.3; color: ${BRAND.inkSoft}; margin: 0 0 12px; padding-left: 12px; border-left: 2px solid ${BRAND.gold}; }
  .fam-foot { text-align: center; font-size: 0.76rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${BRAND.inkSoft}; margin-top: 18px; }
  .fam-foot a { color: ${BRAND.gold}; }
  @media (max-width: 560px) {
    .fam-grid { grid-template-columns: 1fr; }
    .fam-head { align-items: flex-start; }
    .fam-head img { width: 52px; height: 52px; }
  }
  @media print {
    html, body, .fam-root, .fam-root > div { background: #fff !important; }
    .fam-root > div { overflow: visible !important; min-height: 0 !important; }
    .fam-root > div > div { padding: 0 !important; max-width: none !important; }
    .fam-root img[aria-hidden] { display: none !important; }
    header[role="banner"], .core-nav-drawer, .no-print { display: none !important; }
    .fam-lesson { box-shadow: none !important; border-color: #ccc !important; }
    .fam-head, .fam-task, .fam-rules li .n { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

export default function FamilyPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const courseFromUser = user?.course?._id || user?.course;
  const courseId = params.get('course') || courseFromUser;
  const onlyLesson = params.get('lesson');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (onlyLesson && !courseId) {
          const { data } = await api.get(`/lessons/${onlyLesson}`);
          if (alive) setLessons(data?.lesson ? [data.lesson] : []);
        } else if (courseId) {
          const { data } = await api.get(`/lessons/course/${courseId}`);
          if (alive) setLessons(Array.isArray(data?.lessons) ? data.lessons : []);
        } else {
          if (alive) setLessons([]);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.error || 'Unable to load the lessons.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [courseId, onlyLesson]);

  const items = useMemo(() => {
    const list = lessons
      .filter((l) => Array.isArray(l.interactiveSlides) && l.interactiveSlides.length > 0)
      .filter((l) => !onlyLesson || String(l._id) === String(onlyLesson))
      .map((l) => ({ lesson: l, data: extract(l) }))
      .filter(({ data }) => data.homeTasks.length || data.glossary.length || data.recap.length || data.rules.length);
    return list;
  }, [lessons, onlyLesson]);

  const studentName = user?.displayName || user?.name || user?.username || '';
  const courseTitle = typeof user?.course === 'object' ? user.course.title : '';

  return (
    <div className="fam-root">
      <style>{css}</style>
      <Page tone="light" bg={null} maxWidth={880}>
        <div className="fam-top no-print">
          <Button light variant="ghost" size="sm" to="/lezioni"><FiArrowLeft /> Back to Lessons</Button>
          <Button light variant="navy" onClick={() => window.print()}><FiPrinter /> Print / Save as PDF</Button>
        </div>

        <Card light style={{ marginBottom: 22, padding: '22px 26px' }}>
          <header className="fam-head">
            <img src={BRAND.owl} alt="C.O.R.E." />
            <div style={{ minWidth: 0 }}>
              <div className="fam-wordmark">C.O.R.E. for Schools</div>
              <h1>What {studentName ? `${studentName} ` : 'your child '}learned in Cyber Security</h1>
              <Serif light style={{ fontSize: '1.1rem' }}>{courseTitle ? `${courseTitle} · ` : ''}Two minutes to read, one small mission to do together at home.</Serif>
            </div>
          </header>
        </Card>

        {loading && (
          <div style={{ background: BRAND.navy900, borderRadius: 22, border: `1px solid ${BRAND.line}` }}>
            <LoadingSpinner message="Preparing the family page…" />
          </div>
        )}
        {!loading && error && <Alert light>{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <EmptyState light icon="🏠" title="No family page yet" text="Come back after the first interactive lesson — the home missions will appear here." />
        )}

        {items.map(({ lesson, data }, idx) => (
          <Card light className="fam-lesson" key={lesson._id} style={{ padding: '22px 26px' }}>
            <Kicker light style={{ fontSize: '0.68rem' }}>Lesson {idx + 1}</Kicker>
            <h2>{lesson.title}</h2>
            {lesson.description && <p className="desc">{lesson.description}</p>}
            {data.quotes[0] && <p className="fam-quote">“{data.quotes[0].text}”{data.quotes[0].by ? ` — ${CHAR_NAMES[data.quotes[0].by] || data.quotes[0].by}` : ''}</p>}

            {data.homeTasks.length > 0 && (
              <div className="fam-sec">
                <h3>🏠 Mission at home</h3>
                {data.homeTasks.map((t, i) => <div className="fam-task" key={i}><span className="chk" />{t}</div>)}
              </div>
            )}

            {data.recap.length > 0 && (
              <div className="fam-sec">
                <h3>📒 In short</h3>
                <div className="fam-grid">
                  {data.recap.map((r, i) => <div className="fam-card" key={i}><b>{r.title}</b><span>{r.text}</span></div>)}
                </div>
              </div>
            )}

            {data.rules.length > 0 && (
              <div className="fam-sec">
                <h3>⭐ Golden rules for the whole family</h3>
                <ol className="fam-rules">
                  {data.rules.map((r, i) => <li key={i}><span className="n">{i + 1}</span><div><b>{r.title}</b>{r.sub ? <> — <span>{r.sub}</span></> : null}</div></li>)}
                </ol>
              </div>
            )}

            {data.glossary.length > 0 && (
              <div className="fam-sec">
                <h3>📖 Words of the day</h3>
                <div className="fam-words">
                  {data.glossary.map((g, i) => <div className="fam-word" key={i}><b>{g.term}</b>{g.def ? <span> — {g.def}</span> : null}</div>)}
                </div>
              </div>
            )}
          </Card>
        ))}

        <p className="fam-foot">C.O.R.E. for Schools · <a href="https://www.core-aiowl.org" target="_blank" rel="noreferrer">core-aiowl.org</a></p>
      </Page>
    </div>
  );
}
